import * as z from "zod/v4";

/** IMS subscription pricing tiers based on annual revenue */
export const IMS_PRICING_TIERS = [
  { revenueThreshold: 0, monthlyInCents: 5000 },
  { revenueThreshold: 1_000_000, monthlyInCents: 15_000 },
  { revenueThreshold: 5_000_000, monthlyInCents: 56_500 },
  { revenueThreshold: 10_000_000, monthlyInCents: 107_500 },
  { revenueThreshold: 20_000_000, monthlyInCents: 210_000 },
  { revenueThreshold: 30_000_000, monthlyInCents: 312_500 },
  { revenueThreshold: 40_000_000, monthlyInCents: 399_900 },
  { revenueThreshold: 50_000_000, monthlyInCents: null }, // Enterprise - contact sales
] as const;

/** Forecasting costs */
export const FORECASTING_MONTHLY_COST_CENTS = 75_000; // $750/month

/** Annual billing discount percentage */
export const ANNUAL_BILLING_DISCOUNT = 0.15; // 15% discount

/** Annual cost computed from monthly cost with discount applied */
export const FORECASTING_ANNUAL_COST_CENTS =
  FORECASTING_MONTHLY_COST_CENTS * 12 * (1 - ANNUAL_BILLING_DISCOUNT);

const PricingTierSchema = z.object({
  revenueThreshold: z.number(),
  monthlyInCents: z.number().nullable(),
  revenue: z.string(),
  monthly: z.string(),
});

export type PricingTier = z.infer<typeof PricingTierSchema>;

export const getImsPricingInfo = {
  name: "getImsPricingInfo" as const,
  input: z.object({}),
  output: z.object({
    /** Annual revenue estimate, or null if no data available */
    annualRevenue: z.number().nullable(),
    /** Whether revenue data is available */
    revenueDataAvailable: z.boolean(),
    /** Index of the matching pricing tier, or null if no data */
    matchingTierIndex: z.number().nullable(),
    /** Whether the merchant falls into the enterprise tier (requires sales contact) */
    isEnterpriseTier: z.boolean(),
    /** Whether self-enrollment is blocked (no data or enterprise tier) */
    selfEnrollmentBlocked: z.boolean(),
    /** Reason why self-enrollment is blocked, if applicable */
    selfEnrollmentBlockedReason: z
      .enum(["no_revenue_data", "enterprise_tier"])
      .optional(),
    pricingTiers: z.array(PricingTierSchema),
    forecastingMonthlyCostCents: z.number(),
    forecastingAnnualCostCents: z.number(),
    annualBillingDiscount: z.number(),
  }),
};
